/**
 * Panel listant les produits dont le prix a changé depuis la dernière impression d'étiquette.
 * Permet la sélection multiple et l'impression batch.
 */

import { useState, useEffect } from 'react';
import { labelTemplatesApi } from '../../api/label-templates.api';
import { PriceChangesResponse, LabelTemplate, LabelTemplateType } from '../../types/label-templates.types';
import { getHardwareBridgeStatus, hardwareBridgeZebra, zebraAbsenteIci } from '../../services/printers/HardwareBridgeAdapter';

export function PriceChangesPanel() {
  const [data, setData] = useState<PriceChangesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<'all' | 'price_changed' | 'never_printed'>('all');
  const [search, setSearch] = useState('');
  const [printing, setPrinting] = useState(false);
  const [templates, setTemplates] = useState<LabelTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  useEffect(() => {
    loadData();
    loadTemplates();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await labelTemplatesApi.getPriceChanges();
      setData(result);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const allTemplates = await labelTemplatesApi.findAll();
      setTemplates(allTemplates);
      const defaultShelf = allTemplates.find(t => t.type === LabelTemplateType.SHELF && t.is_default);
      if (defaultShelf) {
        setSelectedTemplateId(defaultShelf.id);
      } else if (allTemplates.length > 0) {
        setSelectedTemplateId(allTemplates[0].id);
      }
    } catch {
      // Silencieux
    }
  };

  const filteredProducts = (data?.products || []).filter(p => {
    if (filter !== 'all' && p.status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        p.name.toLowerCase().includes(q) ||
        (p.code && p.code.toLowerCase().includes(q)) ||
        (p.barcode && p.barcode.toLowerCase().includes(q))
      );
    }
    return true;
  });

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredProducts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredProducts.map(p => p.id)));
    }
  };

  const formatPrice = (cents: number | null) => {
    if (cents === null || cents === undefined) return '—';
    return `${(cents / 100).toFixed(2)} €`;
  };

  const handleBatchPrint = async () => {
    if (selectedIds.size === 0 || !selectedTemplateId) return;

    setPrinting(true);
    try {
      const labels = filteredProducts
        .filter(p => selectedIds.has(p.id))
        .map(p => ({
          productId: p.id,
          templateId: p.default_template_id || selectedTemplateId,
          quantity: 1,
        }));

      // Essayer le Hardware Bridge d'abord
      const bridgeStatus = await getHardwareBridgeStatus();
      const useLocalBridge = bridgeStatus?.hardware?.zebra?.available ?? false;
      const absente = zebraAbsenteIci(bridgeStatus);
      if (absente) throw new Error(absente);

      if (useLocalBridge) {
        let successCount = 0;
        for (const label of labels) {
          try {
            const preview = await labelTemplatesApi.preview(label);
            const zpl = (preview as any).zpl;
            if (zpl) {
              const result = await hardwareBridgeZebra.printZPL(zpl);
              if (result.success) successCount++;
            }
          } catch {
            // Continuer avec les suivants
          }
        }
        alert(`${successCount}/${labels.length} étiquette(s) imprimée(s) via Hardware Bridge`);
      } else {
        const result = await labelTemplatesApi.printBatch({ labels }) as any;
        alert(`${result.totalSuccessful ?? 0}/${result.totalRequested ?? labels.length} étiquette(s) imprimée(s)`);
      }

      // Recharger les données
      await loadData();
      setSelectedIds(new Set());
    } catch (err: any) {
      alert(err.response?.data?.message || err.message || 'Erreur lors de l\'impression');
    } finally {
      setPrinting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        <span className="ml-3 text-gray-500">Analyse des prix en cours...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
        <button onClick={loadData} className="mt-2 text-sm text-red-600 underline">
          Réessayer
        </button>
      </div>
    );
  }

  const summary = data?.summary;

  return (
    <div>
      {/* Résumé */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white border rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">{summary?.total || 0}</div>
          <div className="text-sm text-gray-500">Total à ré-étiqueter</div>
        </div>
        <div className="bg-white border rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-orange-600">{summary?.total_price_changed || 0}</div>
          <div className="text-sm text-gray-500">Prix modifiés</div>
        </div>
        <div className="bg-white border rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">{summary?.total_never_printed || 0}</div>
          <div className="text-sm text-gray-500">Jamais étiquetés</div>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="flex gap-1">
          {(['all', 'price_changed', 'never_printed'] as const).map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-sm rounded-md ${
                filter === f
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {f === 'all' ? 'Tous' : f === 'price_changed' ? 'Prix modifiés' : 'Jamais étiquetés'}
            </button>
          ))}
        </div>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Rechercher..."
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-md flex-1 min-w-[200px]"
        />
        <button
          onClick={loadData}
          className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md"
        >
          Actualiser
        </button>
      </div>

      {/* Template + actions batch */}
      {filteredProducts.length > 0 && (
        <div className="flex items-center gap-3 mb-4 bg-gray-50 p-3 rounded-lg">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={selectedIds.size === filteredProducts.length && filteredProducts.length > 0}
              onChange={toggleSelectAll}
              className="rounded border-gray-300 text-indigo-600"
            />
            <span className="text-sm text-gray-600">Tout sélectionner</span>
          </label>
          <div className="flex-1" />
          <select
            value={selectedTemplateId}
            onChange={e => setSelectedTemplateId(e.target.value)}
            className="px-2 py-1.5 text-sm border border-gray-300 rounded-md"
          >
            {templates.map(t => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.width_mm}x{t.height_mm}mm)
              </option>
            ))}
          </select>
          <button
            onClick={handleBatchPrint}
            disabled={printing || selectedIds.size === 0 || !selectedTemplateId}
            className="px-4 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {printing
              ? 'Impression...'
              : `Imprimer ${selectedIds.size > 0 ? `(${selectedIds.size})` : ''}`}
          </button>
        </div>
      )}

      {/* Liste des produits */}
      {filteredProducts.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          {data?.summary?.total === 0
            ? 'Toutes les étiquettes sont à jour !'
            : 'Aucun produit ne correspond aux filtres.'}
        </div>
      ) : (
        <div className="space-y-1">
          {filteredProducts.map(product => (
            <div
              key={product.id}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border ${
                selectedIds.has(product.id) ? 'bg-indigo-50 border-indigo-200' : 'bg-white border-gray-200'
              } hover:border-indigo-300 cursor-pointer`}
              onClick={() => toggleSelect(product.id)}
            >
              <input
                type="checkbox"
                checked={selectedIds.has(product.id)}
                onChange={() => toggleSelect(product.id)}
                onClick={e => e.stopPropagation()}
                className="rounded border-gray-300 text-indigo-600"
              />

              {/* Infos produit */}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate">{product.name}</div>
                <div className="text-xs text-gray-500">
                  {product.code && <span>{product.code}</span>}
                  {product.category_name && <span> · {product.category_name}</span>}
                </div>
              </div>

              {/* Prix */}
              <div className="text-right shrink-0">
                {product.status === 'price_changed' ? (
                  <>
                    <div className="text-xs text-gray-400 line-through">
                      {formatPrice(product.last_printed_price_cents)}
                    </div>
                    <div className="font-semibold text-gray-900">
                      {formatPrice(product.current_price_cents)}
                    </div>
                  </>
                ) : (
                  <div className="font-semibold text-gray-900">
                    {formatPrice(product.current_price_cents)}
                  </div>
                )}
              </div>

              {/* Badge */}
              <div className="shrink-0">
                {product.status === 'price_changed' ? (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    product.price_diff_cents > 0
                      ? 'bg-red-100 text-red-700'
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {product.price_diff_cents > 0 ? '+' : ''}{product.price_diff_percent}%
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                    Nouveau
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
